import React, { useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { func } from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { IconSpinner } from '..';
import config from '../../config';
import { checkLandownerInsurancePromoCode } from '../../util/api';
import { parseInsuranceApiError } from '../../util/landownerInsuranceEnrollmentErrors';
import { propTypes } from '../../util/types';
import {
  getInsuranceCoverageEndDate,
  getInsuranceStatus,
  INSURANCE_STATUSES,
} from '../../util/user';
import NamedLink from '../NamedLink/NamedLink';
import CoverageSummary from './CoverageSummary';
import EnrollmentPaymentForm from './EnrollmentPaymentForm';
import InsurancePaymentMethodUpdateForm from './InsurancePaymentMethodUpdateForm';
import {
  formatCoverageDate,
  formatInsurancePrice,
  getDefaultPaymentMethodLabel,
  getInsuranceCoverageStartDate,
  getInsurancePaymentMethodLabel,
  getLandownerInsuranceBasePrice,
  hasInsurancePaymentIssue,
  INSURANCE_REFRESH_STATUSES,
  INSURANCE_STATUS_MAX_REFRESH_ATTEMPTS,
  INSURANCE_STATUS_POLL_INTERVAL_MS,
  PROMO_CODE_ERROR_MESSAGE_IDS,
  stripePromise,
  wait,
} from './landownerInsuranceHelpers';
import css from './LandownerInsurance.module.css';

const LandownerInsurance = ({ currentUser, intl, onCreateSetupIntent, onRefreshCurrentUser }) => {
  const insuranceStatus = useMemo(() => getInsuranceStatus(currentUser), [currentUser]);
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [promoCodeAdjustment, setPromoCodeAdjustment] = useState(null);
  const [promoCodeError, setPromoCodeError] = useState(null);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);
  const [isLoadingPaymentUpdateForm, setIsLoadingPaymentUpdateForm] = useState(false);
  const [paymentUpdateSetupIntent, setPaymentUpdateSetupIntent] = useState(null);
  const [paymentUpdateFormError, setPaymentUpdateFormError] = useState(null);
  const formattedCoverageEndDate = useMemo(() => {
    const coverageEndDate = getInsuranceCoverageEndDate(currentUser);

    return formatCoverageDate(coverageEndDate);
  }, [currentUser]);
  const formattedCoverageStartDate = useMemo(() => {
    const coverageStartDate = getInsuranceCoverageStartDate(currentUser);

    return formatCoverageDate(coverageStartDate);
  }, [currentUser]);

  const enrollmentAmount = promoCodeAdjustment?.price ?? getLandownerInsuranceBasePrice();
  const requiresPayment = enrollmentAmount > 0;
  const formattedBasePrice = formatInsurancePrice(intl, getLandownerInsuranceBasePrice());
  const hasPaymentIssue = useMemo(() => hasInsurancePaymentIssue(currentUser), [currentUser]);
  const paymentMethodLabel = useMemo(
    () => getInsurancePaymentMethodLabel(currentUser) || getDefaultPaymentMethodLabel(currentUser),
    [currentUser]
  );
  // Keep the Elements amount pinned to the full base price so the Payment Element is
  // never remounted when a promo code is applied (which would wipe entered card details).
  // The discount is applied server-side via the promotion code, and the resulting
  // PaymentIntent reflects the adjusted amount at confirmation time.
  const stripeOptions = useMemo(
    () => ({
      mode: 'subscription',
      amount: getLandownerInsuranceBasePrice(),
      currency: config.currency.toLowerCase(),
      appearance: config.stripe.appearance,
    }),
    []
  );

  const handleApplyPromoCode = async promoCode => {
    setPromoCodeError(null);

    try {
      const adjustment = await checkLandownerInsurancePromoCode({ promoCode });
      setPromoCodeAdjustment(adjustment);
      return adjustment;
    } catch (error) {
      const apiError = parseInsuranceApiError(error);
      const messageId = PROMO_CODE_ERROR_MESSAGE_IDS[(apiError?.message)];

      setPromoCodeAdjustment(null);
      setPromoCodeError(messageId ? intl.formatMessage({ id: messageId }) : null);
      throw error;
    }
  };

  const handleEnrollmentComplete = async () => {
    setEnrollmentSuccess(false);
    setIsRefreshingStatus(true);

    try {
      for (let attempt = 0; attempt < INSURANCE_STATUS_MAX_REFRESH_ATTEMPTS; attempt += 1) {
        const refreshedUser = await onRefreshCurrentUser();
        const refreshedStatus = getInsuranceStatus(refreshedUser);

        if (INSURANCE_REFRESH_STATUSES.includes(refreshedStatus)) {
          setIsRefreshingStatus(false);
          return;
        }

        if (attempt < INSURANCE_STATUS_MAX_REFRESH_ATTEMPTS - 1) {
          await wait(INSURANCE_STATUS_POLL_INTERVAL_MS);
        }
      }

      setEnrollmentSuccess(true);
    } catch {
      setEnrollmentSuccess(true);
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const handleShowPaymentMethodUpdate = async () => {
    setIsUpdatingPaymentMethod(true);
    setPaymentUpdateFormError(null);

    if (paymentUpdateSetupIntent?.clientSecret) {
      return;
    }

    setIsLoadingPaymentUpdateForm(true);

    try {
      const setupIntent = await onCreateSetupIntent();
      const setupIntentAttributes = setupIntent?.attributes || setupIntent;

      if (!setupIntentAttributes?.clientSecret) {
        throw new Error('Unable to start payment method update.');
      }

      setPaymentUpdateSetupIntent(setupIntentAttributes);
    } catch (error) {
      setPaymentUpdateFormError(
        error?.message || intl.formatMessage({ id: 'LandownerInsurance.updatePaymentError' })
      );
    } finally {
      setIsLoadingPaymentUpdateForm(false);
    }
  };

  const handlePaymentMethodUpdated = async () => {
    setPaymentUpdateFormError(null);
    await onRefreshCurrentUser();
    setPaymentUpdateSetupIntent(null);
    setIsUpdatingPaymentMethod(false);
  };

  const showEnrollmentForm =
    !enrollmentSuccess &&
    !isRefreshingStatus &&
    !hasPaymentIssue &&
    ![INSURANCE_STATUSES.ACTIVE, INSURANCE_STATUSES.PENDING].includes(insuranceStatus);
  const showPaymentUpdateForm =
    isUpdatingPaymentMethod &&
    !isLoadingPaymentUpdateForm &&
    paymentUpdateSetupIntent?.clientSecret;
  const paymentUpdateStripeOptions = paymentUpdateSetupIntent?.clientSecret
    ? {
        clientSecret: paymentUpdateSetupIntent.clientSecret,
        appearance: config.stripe.appearance,
      }
    : null;

  return (
    <div className={css.root}>
      <div className={`${css.banner} ${css.sectionIntro}`}>
        <h2 className={css.sectionTitle}>
          <FormattedMessage id="LandownerInsurance.title" />
        </h2>
        <div className={css.bannerDescription}>
          <FormattedMessage
            id="LandownerInsurance.bannerDescription"
            values={{
              coverageLink: (
                <NamedLink className={css.inlineLink} name="InsurancePage" target="_blank">
                  <FormattedMessage id="LandownerInsurance.learnMore" />
                </NamedLink>
              ),
            }}
          />
        </div>
      </div>

      <div className={css.divider} />

      {showEnrollmentForm ? (
        <Elements stripe={stripePromise} options={stripeOptions}>
          <EnrollmentPaymentForm
            currentUser={currentUser}
            intl={intl}
            onApplyPromoCode={handleApplyPromoCode}
            onEnrollmentComplete={handleEnrollmentComplete}
            promoCodeAdjustment={promoCodeAdjustment}
            promoCodeError={promoCodeError}
            requiresPayment={requiresPayment}
          />
        </Elements>
      ) : null}

      <CoverageSummary
        enrollmentSuccess={enrollmentSuccess}
        formattedCoverageEndDate={formattedCoverageEndDate}
        formattedCoveragePrice={formattedBasePrice}
        formattedCoverageStartDate={formattedCoverageStartDate}
        hasPaymentIssue={hasPaymentIssue}
        insuranceStatus={insuranceStatus}
        isRefreshingStatus={isRefreshingStatus}
        onShowPaymentMethodUpdate={handleShowPaymentMethodUpdate}
        paymentMethodLabel={paymentMethodLabel}
      />

      {isUpdatingPaymentMethod ? (
        <div className={css.paymentUpdateWrapper}>
          {isLoadingPaymentUpdateForm ? (
            <div className={css.inlineLoader}>
              <IconSpinner className={css.inlineSpinnerIcon} />
            </div>
          ) : null}
          {paymentUpdateFormError ? (
            <div className={css.errorMessage}>{paymentUpdateFormError}</div>
          ) : null}
          {showPaymentUpdateForm ? (
            <Elements stripe={stripePromise} options={paymentUpdateStripeOptions}>
              <InsurancePaymentMethodUpdateForm
                currentUser={currentUser}
                intl={intl}
                onCancel={() => setIsUpdatingPaymentMethod(false)}
                onPaymentMethodUpdated={handlePaymentMethodUpdated}
              />
            </Elements>
          ) : null}
        </div>
      ) : null}

      <div className={`${css.section} ${css.sectionIntro}`}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id="PaymentMethods.needHelp" />
        </h3>
        <div className={css.supportText}>
          <FormattedHTMLMessage id="PaymentMethods.callUs" />
        </div>
      </div>
    </div>
  );
};

LandownerInsurance.defaultProps = {
  currentUser: null,
  onCreateSetupIntent: () => Promise.resolve(null),
  onRefreshCurrentUser: () => Promise.resolve(null),
};

LandownerInsurance.propTypes = {
  currentUser: propTypes.currentUser,
  intl: intlShape.isRequired,
  onCreateSetupIntent: func,
  onRefreshCurrentUser: func,
};

LandownerInsurance.displayName = 'LandownerInsurance';

export default injectIntl(LandownerInsurance);
